import { getLayerFromElement, showWarningDialog } from "#skyward_modalLayer";
import { setClass } from "./skyward.ui.buttonManager";
import { clearDirtyList, isDirty } from "./skyward.ui.changeTracking";
import { getSvg } from "./skyward.ui.svg";
import { DetailsPanelAdditionalEventData, DetailsPanelCallbackStore, DetailsPanelData, DetailsPanelEventData, DetailsPanelEvents, DetailsPanelOptions, Dictionary, Skyward } from "./types";

window.skyward.base.registerScript("detailsPanel", function (skyward: Skyward, $: JQueryStatic)
{
    let detailsAjaxHandle: JQuery.jqXHR<any> | JQueryPromise<any>;
    let detailsPanel: JQuery;
    let moduleContent: JQuery;
    let rootContainer: JQuery;
    let dirtyResizeWarningPromise: JQueryPromise<any> | null = null;
    let lastCheckedWidth = 0;
    let detailsPanelData: DetailsPanelData | null = null;
    let sessionShowDetailsPanel = false; // this is the session setting
    const minimumSupportedSplitScreenWidth = 1200;

    const classNames =
    {
        detailsPanelCloseBar: "detailsPanel__closeBar",
        previewModeOn: "previewModeOn",
        detailsPanel: "detailsPanel",
        skywardContentPanel: "skywardContentPanel",
        changeTrackingRoot: "js-changeTrackingRoot",
        currentRecordContent: "currentRecordContent",
        button: "button",
    };
    const dataNames =
    {
        showDetailsPanel: "show-details-panel",
    };
    const idNames =
    {
        detailsPanel: "detailsPanel",
        moduleContent: "moduleContent",
        dirtyDetailsResizeModal: "DirtyDetailsResizeWarning",
    };
    const getLazyCloseToolbar = skyward.extensions.lazy(function ()
    {
        return $("<div>",
            {
                class: classNames.detailsPanelCloseBar,
                append: $("<a>",
                    {
                        class: "button chromeless svgAnchor",
                        href: "javascript:void(0)",
                        append: [getSvg("Close"), " Close"],
                        "data-click-action": "detailsPanel.close"
                    }
                )
            }
        );
    });

    const getCloseToolbar = function ()
    {
        const closeToolbar = getLazyCloseToolbar();
        setClass(closeToolbar.find(classNames.button.dot()), false);
        return closeToolbar;
    };

    const eventCallbacks: DetailsPanelEvents =
    {
        open: skyward.events.getCallbackStore(),
        close: skyward.events.getCallbackStore(),
        resizeClose: skyward.events.getCallbackStore(),
        resizeOpen: skyward.events.getCallbackStore(),
        resizeCanOpen: skyward.events.getCallbackStore(),
        empty: skyward.events.getCallbackStore(),
        contentLoaded: skyward.events.getCallbackStore(),
        resizeThresholdCrossed: skyward.events.getCallbackStore(),
    };

    const events =
    {
        init: init,
        close: close
    };

    const defaultOptions: DetailsPanelOptions =
    {
        shouldAnimate: true,
        panelWidth: undefined,
        forcePopup: false,
        useCloseButton: false,
        additionalData: {}
    };

    skyward.detailsPanel = skyward.detailsPanel || {};

    skyward.detailsPanel.eventCallbacks = skyward.events.getCallbackStoreFacadeCollection(eventCallbacks);

    /**
     * Returns the current session state whether browse row details panels should open.
     * @returns {boolean} True if the session state is true (indicating row details panels should open)
     */
    skyward.detailsPanel.getDetailsPanelSessionState = function ()
    {
        return sessionShowDetailsPanel;
    };

    /**
     * Sets and saves the session state property for browse row details panels.
     * @param {boolean} isVisible - The new boolean value for the session state
     */
    skyward.detailsPanel.setDetailsPanelSessionState = function (isVisible: boolean)
    {
        if (!hasInitialized())
        {
            return;
        }
        saveDetailsPanelState(!!isVisible);
    };

    /**
     * Is a details panel currently open and displaying on the screen?
     * @returns {boolean} True if a details panel is open
     */
    skyward.detailsPanel.isDetailsPanelOpen = function ()
    {
        return hasInitialized() && isDetailsPanelSet();
    };

    /**
     * Gets all of the data that was supplied when the currently displayed details panel was opened.
     * @returns {object} The resulting data object
     */
    skyward.detailsPanel.getData = getData;

    /**
     * Get the additional data object that was set when the currently displayed details panel was opened.
     * @returns {object} The additional data object that was passed to the current details panel
     */
    skyward.detailsPanel.getAdditionalData = getAdditionalData;

    /**
     * Open a details panel.
     * @param {string} module - The name of the module to call when requesting the details content
     * @param {string} object - The name of the object/controller to call when requesting the details content
     * @param {string} screen - The name of the screen/action to call when requesting the details content
     * @param {string} id - The id/primaryKey to pass on the url when requesting the details content
     * @param {object} data - The data/postData to send when requesting the details content
     * @param {object} optionsArg - Optional settings for the details panel
     * @returns {Promise} a promise that is resolved when/if the panel opens (which may be before the content is loaded)
     */
    skyward.detailsPanel.openDetailsPanel = function (module: string, object: string, screen: string, id: string, data?: any, optionsArg?: DetailsPanelOptions)
    {
        const deferred = $.Deferred();
        if (!hasInitialized() || module == null || object == null || screen == null)
        {
            return deferred.reject().promise();
        }

        const options = $.extend({}, defaultOptions, optionsArg);

        if (canShowDetailsPanel() && !options.forcePopup)
        {
            return skyward.detailsPanel.tryCloseDetailsPanel(() =>
            {
                emptyDetailsPanel();
                setDetailsPanelData(module, object, screen, id, data, options);
                toggleDetailsPanelDisplay(true, options.shouldAnimate, options.panelWidth);

                if (isJqXhr(detailsAjaxHandle))
                {
                    detailsAjaxHandle.abort();
                }

                detailsAjaxHandle = skyward.window.loadContent("detailsPanel", module, object, screen, id, data);

                detailsAjaxHandle.then(function ()
                {
                    if (options.useCloseButton === true)
                    {
                        detailsPanel.prepend(getCloseToolbar());
                    }
                });
                return detailsAjaxHandle.promise();
            });
        }
        else
        {
            skyward.window.loadContent("popout", module, object, screen, id, data);
        }

        return deferred.reject().promise();
    };

    /**
     * Close the details panel if it is currently open.
     * @returns {JQueryPromise} A Promise that is resolved when/if the panel closes
     */
    skyward.detailsPanel.closeDetailsPanel = function (shouldAnimate)
    {
        if (!hasInitialized() || !isDetailsPanelSet())
        {
            return $.Deferred().reject().promise();
        }
        return skyward.detailsPanel.tryCloseDetailsPanel(function ()
        {
            return toggleDetailsPanelDisplay(false, shouldAnimate);
        });
    };

    /**
     * Is the window wide enough to show the details panel?
     * @returns {boolean} True if the window is wide enough to show the details panel
     */
    skyward.detailsPanel.canShowDetailsPanel = canShowDetailsPanel;

    /**
     * Clears the contents of the details panel.
     */
    skyward.detailsPanel.emptyDetailsPanel = emptyDetailsPanel;

    /**
     * Checks if panels have unsaved changes
     * @returns {boolean} True when either panel is dirty (has pending changes)
     */
    skyward.detailsPanel.detailsPanelIsDirty = function ()
    {
        if (!hasInitialized() || !isDetailsPanelSet())
        {
            return false;
        }

        const dov = detailsPanel.find(classNames.currentRecordContent.dot());
        if (dov.length < 1)
        {
            return false;
        }

        return isDirty(dov);
    };

    /**
     * Opens a warning dialog for the user to confirm unsaved data loss or cancel.
     * @param {function} onCloseCallback - A function to call when the confirm action is taken.
     * @returns {Promise} A Promise that resolves when the warning dialog closes.
     */
    skyward.detailsPanel.tryCloseDetailsPanel = (function ()
    {
        let tryCallbacks: Array<() => JQueryPromise<any> | void> = [];
        let tryDeferred: JQueryDeferred<any> | undefined;

        return function (onCloseCallback: () => JQueryPromise<any> | void): JQueryPromise<boolean>
        {
            tryCallbacks.push(onCloseCallback);
            if (tryDeferred !== undefined)
            {
                return tryDeferred.promise();
            }
            tryDeferred = $.Deferred();
            let wasDirty = false;

            if (skyward.detailsPanel.detailsPanelIsDirty())
            {
                showDirtyDetailsPanelWarning(async function ()
                {
                    wasDirty = true;
                    await clearDetailsPanelDirtyData();
                    complete();
                }).fail(cancel);
            }
            else
            {
                complete();
            }

            return (tryDeferred || $.Deferred().reject()).promise();

            function cancel()
            {
                tryCallbacks = [];
                tryDeferred?.reject(wasDirty);
                tryDeferred = undefined;
            }

            function complete()
            {
                let additionalDeferreds = [];
                let callbacksToCall = tryCallbacks;
                tryCallbacks = [];
                for (let i = 0; i < callbacksToCall.length; i++)
                {
                    if (typeof callbacksToCall[i] === "function")
                    {
                        const response = callbacksToCall[i]();
                        if (typeof response === "object" && typeof response.done === "function")
                        {
                            additionalDeferreds.push(response);
                        }
                    }
                }

                $.whenAllSettled(additionalDeferreds).done(function ()
                {
                    // If additional calls were made while waiting for the
                    // additional deferreds to resolve, call those callbacks
                    if (tryCallbacks.length > 0)
                    {
                        complete();
                        return;
                    }
                    tryDeferred?.resolve(wasDirty);
                    tryDeferred = undefined;
                }).fail(cancel);
            }
        };
    })();

    /**
     * Checks if the detail panel contains a given element.
     * @param {jQuery} element - An element to find within the details panel
     * @returns {boolean} True when the given element is contained within the details panel
     */
    skyward.detailsPanel.containsElement = function (element)
    {
        if (!hasInitialized() || $.isEmptyObject(element))
        {
            return false;
        }
        return isDetailsPanelSet() && detailsPanel.get(0)?.contains(element.get(0) ?? null) || false;
    };

    /**
     * Allows setting the width of the details panel
     * @param width the new width of the details panel
     */
    skyward.detailsPanel.setPanelWidth = function (width: number | string)
    {
        if (hasInitialized() && isDetailsPanelSet())
        {
            detailsPanel.css("width", width);
        }
    }

    skyward.detailsPanel.addClassToDetailsPanel = function (className: string)
    {
        detailsPanel.addClass(className);
    }

    skyward.detailsPanel.addClassToModuleContent = function (className: string)
    {
        moduleContent.addClass(className);
    }

    skyward.detailsPanel.setDetailsPanelData = function (module: string, object: string, screen: string, id: string, postData: Dictionary<string, string>)
    {
        setDetailsPanelData(module, object, screen, id, postData, detailsPanelData?.options ?? { ...defaultOptions });
    };

    return events;

    function init(this: JQuery)
    {
        // Don't allow multiple initializations
        if (hasInitialized())
        {
            console.warn("Multiple calls to detailsPanel.init, which should only be called once.", rootContainer, this);
            return;
        }
        rootContainer = $(this);
        moduleContent = rootContainer.find("#" + idNames.moduleContent);

        sessionShowDetailsPanel = rootContainer.data(dataNames.showDetailsPanel) === "True";

        eventCallbacks.open.registerCallback(function (_data: DetailsPanelEventData, additionalData: DetailsPanelAdditionalEventData)
        {
            if (additionalData.opener)
            {
                additionalData.opener.scrollintoview(
                    {
                        duration: 0
                    });
            }
        });

        initDetailsPanel();

        skyward.window.registerLayoutResizeCallback(detailsPanelWindowResizeHandler);
    }

    function hasInitialized()
    {
        return rootContainer && moduleContent && detailsPanel;
    }

    function close()
    {
        const additionalData = getAdditionalData();
        skyward.detailsPanel.closeDetailsPanel(true).then(function ()
        {
            if (additionalData.opener)
            {
                additionalData.opener.focus();
            }
        });
    }

    function dispatchCallbacks(callbackStore: DetailsPanelCallbackStore, data?: any)
    {
        const eventData: DetailsPanelEventData = $.extend(getData(), data);
        const additionalData = getAdditionalData();
        callbackStore.dispatchCallbacks(eventData, additionalData);
    }

    function setDetailsPanelData(module: string, object: string, screen: string, id: string, data: Partial<DetailsPanelData>, options: DetailsPanelOptions)
    {
        detailsPanelData = $.extend({}, data, options,
            {
                renderInDetailsPanel: true,
                module: module,
                object: object,
                screen: screen,
                primarykey: id,
                postData: data,
                options: options
            }
        );
    }

    function resetDetailsPanelData()
    {
        detailsPanelData = null;
    }

    function getData()
    {
        return $.extend({}, detailsPanelData);
    }

    function emptyDetailsPanel()
    {
        if (!hasInitialized() || detailsPanel.is(":empty"))
        {
            return;
        }
        detailsPanel.empty();
        dispatchCallbacks(eventCallbacks.empty);
    }

    function setDetailsPanel()
    {
        if (isDetailsPanelSet())
        {
            return;
        }

        moduleContent.addClass(classNames.previewModeOn);
        rootContainer.append(detailsPanel);
    }

    function unsetDetailsPanel(partialUnset?: boolean)
    {
        if (!isDetailsPanelSet())
        {
            return;
        }

        detailsPanel.detach();
        moduleContent.removeClass(classNames.previewModeOn);

        if (!partialUnset)
        {
            emptyDetailsPanel();
        }
    }

    function canShowDetailsPanel()
    {
        return skyward.window.clientWidth >= minimumSupportedSplitScreenWidth;
    }

    function couldShowDetailsPanel(width: number)
    {
        return width >= minimumSupportedSplitScreenWidth;
    }

    function toggleDetailsPanelDisplay(display: boolean, shouldAnimate?: boolean, targetWidth?: number)
    {
        const deferred = $.Deferred();

        if (display && !canShowDetailsPanel())
        {
            return deferred.reject().promise();
        }

        if (display)
        {
            setDetailsPanel();
        }

        const animationDuration = shouldAnimate ? 230 : 0;
        const panelWidth = display ? (targetWidth ?? "50%") : 0;

        detailsPanel.stop().animate({ width: panelWidth }, animationDuration, afterAnimate);

        return deferred.promise();

        function afterAnimate()
        {
            if (!display)
            {
                unsetDetailsPanel();
            }
            $(window).trigger("resize");
            if (display)
            {
                dispatchCallbacks(eventCallbacks.open);
            }
            else
            {
                dispatchCallbacks(eventCallbacks.close);
                resetDetailsPanelData();
            }
            deferred.resolve();
        }
    }

    function showDirtyDetailsPanelWarningForResize()
    {
        const actionText = "use a smaller window size";
        const descriptionText = "The record details has unsaved changes and cannot display at this window size.\n\n" +
            "Your unsaved data will be lost if you " + actionText + ".\n\n" +
            "Resize your window to be wider to keep these changes.\n\n" +
            "This message will close when your window is wide enough to show the record details.";

        return showWarningDialog(actionText, descriptionText, { id: idNames.dirtyDetailsResizeModal });
    }

    /**
     * Opens a warning dialog for the user to confirm unsaved data loss or cancel.
     * @param {function} onLeaveCallback - A function to call when the confirm action is taken.
     * @returns {JQueryPromise} A Promise that resolves when the warning dialog closes.
     */
    function showDirtyDetailsPanelWarning(onLeaveCallback: () => (any | Promise<any> | void))
    {
        const deferred = $.Deferred();

        if (!hasInitialized())
        {
            return deferred.reject().promise();
        }

        const actionText = "leave this record";
        const descriptionText = "You have unsaved data that will be lost if you " + actionText + ".";

        showWarningDialog(actionText, descriptionText).then(() =>
        {
            if (typeof onLeaveCallback === "function")
            {
                const response = onLeaveCallback();
                $.when(response).done(deferred.resolve);
            }
            else
            {
                deferred.resolve();
            }
        }).catch(() =>
        {
            const detailsPanelSaveButton = detailsPanel.find(".toolbar .save");
            if (detailsPanelSaveButton.length > 0)
            {
                detailsPanelSaveButton.first().focus();
            }
            deferred.reject();
        });

        return deferred.promise();
    };

    /**
     * Clears the dirty state of the panel.
     */
    async function clearDetailsPanelDirtyData()
    {
        if (!hasInitialized())
        {
            return;
        }

        const changeTrackingRoot = detailsPanel.find(classNames.changeTrackingRoot.dot());
        if (changeTrackingRoot.length < 1)
        {
            return;
        }

        const changeTrackingRootId = changeTrackingRoot.attr("id");
        if (changeTrackingRootId !== undefined)
        {
            await clearDirtyList(changeTrackingRootId);
        }
    };

    function saveDetailsPanelState(showDetailsPanelState: boolean)
    {
        sessionShowDetailsPanel = showDetailsPanelState;
        return $.ajax(skyward.global.virtualDir + "Security/Session/UpdateDetailsPanelDisplay",
            {
                type: "POST",
                dataType: "JSON",
                data: { showDetailsPanel: sessionShowDetailsPanel }
            }
        );
    }

    /**
     * Checks if the page is in "display details panel mode".  The details panel is either open, or will open.
     * @returns {boolean} True when the details panel is either open, or will open.
     */
    function isDetailsPanelSet()
    {
        return moduleContent.hasClass(classNames.previewModeOn) || document.documentElement?.contains(detailsPanel.get(0) ?? null) || false;
    }

    function detailsPanelWindowResizeHandler()
    {
        // prevent triggering events unnecessarily if the window size didn't actually change between calls
        if (lastCheckedWidth === skyward.window.clientWidth)
        {
            return;
        }

        const canShowDetailsPanelResult = canShowDetailsPanel();
        const couldShowDetailsPanelResult = couldShowDetailsPanel(lastCheckedWidth);

        lastCheckedWidth = skyward.window.clientWidth;

        if (canShowDetailsPanelResult === couldShowDetailsPanelResult)
        {
            return;
        }

        dispatchCallbacks(eventCallbacks.resizeThresholdCrossed, { canShowDetailsPanel: canShowDetailsPanelResult });

        const detailsPanelIsSet = isDetailsPanelSet();

        if (detailsPanelIsSet && !canShowDetailsPanelResult)
        {
            if (dirtyResizeWarningPromise === null && skyward.detailsPanel.detailsPanelIsDirty())
            {
                dirtyResizeWarningPromise = showDirtyDetailsPanelWarningForResize();
            }
            unsetDetailsPanel(true);
            dispatchCallbacks(eventCallbacks.resizeClose);
        }
        else if (!detailsPanelIsSet && canShowDetailsPanelResult)
        {
            if (!detailsPanel.is(":empty"))
            {
                if (dirtyResizeWarningPromise !== null)
                {
                    closeDirtyResizeWarningModal();
                }
                setDetailsPanel();
                dispatchCallbacks(eventCallbacks.resizeOpen);
            }
            else if (sessionShowDetailsPanel)
            {
                dispatchCallbacks(eventCallbacks.resizeCanOpen);
            }
        }
    }

    function closeDirtyResizeWarningModal()
    {
        dirtyResizeWarningPromise = null;
        const layer = getLayerFromElement($("#" + idNames.dirtyDetailsResizeModal));
        if (layer && typeof layer.close === "function")
        {
            layer.close();
        }
    }

    function initDetailsPanel()
    {
        detailsPanel = $("<div>", { id: idNames.detailsPanel, class: [classNames.detailsPanel, classNames.skywardContentPanel].join(" ") });

        detailsPanel.on("DOMInsertionPostLoad", function (event)
        {
            if (!detailsPanel.is(event.target))
            {
                return;
            }
            dispatchCallbacks(eventCallbacks.contentLoaded);
        });
    }

    function getAdditionalData(): DetailsPanelAdditionalEventData
    {
        const data = getData();
        if (Object.prototype.hasOwnProperty.call(data, "options"))
        {
            return data.options.additionalData ?? {};
        }
        return {};
    }

    function isJqXhr(promise: any): promise is JQueryXHR
    {
        return typeof promise === "object" && typeof (promise as any).abort === "function";
    }
});
